import clsx from "clsx";
import { useState } from "react";
import {
  type ClashChoice,
  firmKey,
  type ImportPageState,
  type ImportPreviewOrg,
} from "../../utils/bpaImport";
import { firmControl } from "../../utils/bpaImportItemControl";
import {
  type ItemLine,
  type ReviewFirm,
  type ReviewGroup,
  itemLines,
  visibleFirms,
} from "../../utils/bpaImportReview";
import { ColouredWords } from "./ColouredWords";
import styles from "./ReviewGroups.module.css";

type ReviewGroupsProps = {
  groups: ReviewGroup[];
  orgs: ImportPreviewOrg[];
  state: ImportPageState;
};

const PLACEHOLDER_GROUPS = 4;

const CHOICE_TEXT: Record<ClashChoice, string> = {
  keep_target: "Keep target's",
  take_source: "Use source's",
};

function changesText(count: number): string {
  return count === 1 ? "1 change" : `${count} changes`;
}

function landingTables(org: ImportPreviewOrg, line: ItemLine): string[] {
  return (org.items?.source ?? [])
    .filter(item => line.itemIds.includes(item.id))
    .filter(item => item.outcome === "new")
    .filter(item => item.kind === "firm_ranking" || item.kind === "lawyer_ranking")
    .filter(item => item.landingTableName)
    .map(item => `${item.landingTableName}${item.landingTableIsNew ? " (new\u00a0table)" : ""}`);
}

function losesTier(org: ImportPreviewOrg, line: ItemLine): boolean {
  return (org.items?.source ?? []).some(
    item => line.itemIds.includes(item.id) && item.losesTier === true,
  );
}

type LineTagsProps = { changed: boolean; landings: string[]; tierLost: boolean };

function LineTags({ changed, landings, tierLost }: LineTagsProps) {
  if (!changed && landings.length === 0 && !tierLost) return null;
  return (
    <span className={styles.after}>
      {changed && <span className={styles.yourChange}>Your change</span>}
      {tierLost && <span className={styles.tierLost}>Loses its tier</span>}
      {landings.map(table => (
        <span key={table} className={styles.landing}>
          Lands in {table}
        </span>
      ))}
    </span>
  );
}

type ItemLinesProps = { org: ImportPreviewOrg; clicked: string[]; leftOut: boolean };

function ItemLines({ org, clicked, leftOut }: ItemLinesProps) {
  return (
    <ul className={styles.lines}>
      {itemLines(org, { leftOut }).map(line => (
        <li
          key={line.itemIds.join("|")}
          className={styles.line}
          data-removed={line.removedWithNothing ? "true" : undefined}
        >
          <span className={styles.kind}>{line.kindLabel}</span>
          <span className={styles.sentence}>
            <ColouredWords text={line.sentence} />
          </span>
          <LineTags
            changed={!leftOut && line.itemIds.some(id => clicked.includes(id))}
            landings={leftOut ? [] : landingTables(org, line)}
            tierLost={!leftOut && losesTier(org, line)}
          />
        </li>
      ))}
    </ul>
  );
}

type FirmRowProps = {
  firm: ReviewFirm;
  org: ImportPreviewOrg | undefined;
  state: ImportPageState;
  expanded: boolean;
  leftOut: boolean;
  onToggle: () => void;
};

function FirmRow({ firm, org, state, expanded, leftOut, onToggle }: FirmRowProps) {
  return (
    <li className={styles.firm}>
      <div className={styles.firmRow}>
        <button
          type="button"
          className={styles.firmButton}
          aria-expanded={expanded}
          onClick={onToggle}
        >
          <span aria-hidden="true" className={styles.chevron}>
            {expanded ? "▾" : "▸"}
          </span>
          <span className={styles.firmName}>{firm.name}</span>
          <span className={styles.firmLine}>{firm.line}</span>
        </button>
        {firm.changes > 0 && <span className={styles.changes}>{changesText(firm.changes)}</span>}
        {firm.choice && (
          <span className={styles.choice} data-choice={firm.choice}>
            {CHOICE_TEXT[firm.choice]}
          </span>
        )}
      </div>
      {expanded && org && (
        <ItemLines org={org} clicked={firmControl(state, firm.key).clicked} leftOut={leftOut} />
      )}
    </li>
  );
}

export function ReviewGroups({ groups, orgs, state }: ReviewGroupsProps) {
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>({});
  const [expandedFirms, setExpandedFirms] = useState<string[]>([]);
  const [shownAll, setShownAll] = useState<string[]>([]);
  const orgByKey = new Map(orgs.map(org => [firmKey(org), org]));

  function isOpen(group: ReviewGroup): boolean {
    return openOverrides[group.id] ?? group.open;
  }

  function toggleGroup(group: ReviewGroup) {
    setOpenOverrides(current => ({ ...current, [group.id]: !isOpen(group) }));
  }

  function toggleFirm(key: string) {
    setExpandedFirms(current =>
      current.includes(key) ? current.filter(existing => existing !== key) : [...current, key],
    );
  }

  return (
    <div className={styles.groups}>
      {groups.map(group => {
        const open = isOpen(group);
        const { firms, showAllLabel } = visibleFirms(group, shownAll.includes(group.id));
        return (
          <section key={group.id} className={styles.group} aria-label={group.title}>
            <button
              type="button"
              className={styles.groupHead}
              aria-expanded={open}
              onClick={() => toggleGroup(group)}
            >
              <span aria-hidden="true" className={styles.chevron}>
                {open ? "▾" : "▸"}
              </span>
              <span
                className={styles.groupTitle}
                data-risky={group.id === "replaces" && group.firms.length > 0 ? "true" : undefined}
              >
                {group.title}
              </span>
              <span className={styles.count}>{group.firms.length}</span>
              {group.note && <span className={styles.note}>{group.note}</span>}
            </button>
            {open && (
              <>
                <ul className={styles.firms}>
                  {firms.map(firm => (
                    <FirmRow
                      key={firm.key}
                      firm={firm}
                      org={orgByKey.get(firm.key)}
                      state={state}
                      expanded={expandedFirms.includes(firm.key)}
                      leftOut={group.id === "leftOut"}
                      onToggle={() => toggleFirm(firm.key)}
                    />
                  ))}
                </ul>
                {showAllLabel && (
                  <button
                    type="button"
                    className={styles.showAll}
                    onClick={() => setShownAll(current => [...current, group.id])}
                  >
                    {showAllLabel}
                  </button>
                )}
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}

export function PlaceholderBar({ className }: { className: string }) {
  return <div className={clsx(styles.placeholderBar, className)} />;
}

export function ReviewGroupsPlaceholder() {
  return (
    <div className={styles.groups}>
      {Array.from({ length: PLACEHOLDER_GROUPS }, (_, index) => (
        <div key={index} className={styles.group} data-placeholder="group">
          <div className={styles.groupHeadPlaceholder}>
            <PlaceholderBar className={styles.groupTitlePlaceholder} />
          </div>
        </div>
      ))}
    </div>
  );
}
